import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate } from 'react-router-dom';
import {
  ShieldCheck, Search, RefreshCw,
  Check, X, CheckCircle2, Circle, Calendar, Download, Building2,
  ChevronDown, AlertTriangle, ExternalLink, Zap, Receipt, ArrowRightLeft,
} from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { formatDate } from '../../src/lib/utils';
import { formatCurrency } from '../../src/lib/comissaoUtils';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { buildSheet, downloadWorkbook, fileStamp } from '../../src/lib/excelExport';
import { fetchGestaoUcKeys, isGestaoFatura } from '../../src/lib/sicoobAccount';
import { isContaSicoob, NOME_CONTA_SICOOB } from '../../src/lib/contaSicoob';
import { buscarTudoPorChave } from '../../src/lib/paginacaoKeyset';
import { resolverLinkBoleto } from '../../src/lib/linkBoleto';
import {
  boletoCobrouEquatorial, escolherItemRepasse, explicarRepasse, traduzirErroRepasse, type TomRepasse,
} from '../../src/lib/repasseEquatorial';
import ConfirmarAuditoriaModal from './ConfirmarAuditoriaModal';
import type { Fatura, EmpresaParceira, PagamentoEquatorialAuditoria, PagamentoEquatorialFila } from '../../types';

// Mantidas as duas permissões antigas (Auditoria de Pagamentos + Auditoria
// Boleto Único, que era uma tela separada) para não invalidar acesso já
// concedido a quem só tinha uma delas — as duas agora abrem esta mesma tela.
export const AUDITORIA_PAGAMENTOS_PERMISSION = 'Auditoria de Pagamentos';
export const AUDITORIA_BOLETO_UNICO_PERMISSION = 'Auditoria Boleto Único';

// Quem entra na auditoria (tela, atalho no FAB e Repasse Equatorial manual): adm ou
// analista com uma das duas permissões. Analista desde 21/10/2026. O banco usa a mesma
// regra (auth_pode_auditar_pagamentos: RLS das NFs e RPCs de repasse) — mude os dois juntos.
export const podeAuditarPagamentos = (
  profile: { role?: string | null; permissions?: string[] | null } | null | undefined,
): boolean =>
  ['adm', 'analista'].includes(profile?.role ?? '') && (
    !!profile?.permissions?.includes(AUDITORIA_PAGAMENTOS_PERMISSION)
    || !!profile?.permissions?.includes(AUDITORIA_BOLETO_UNICO_PERMISSION)
  );

// Data no formato do <input type="date"> (YYYY-MM-DD) em horário local —
// toISOString() aqui adianta ou atrasa um dia dependendo do fuso.
const toInputDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mesAtual = (): { inicio: string; fim: string } => {
  const now = new Date();
  return {
    inicio: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    fim: toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

// Enquanto o ano é digitado o input emite datas parciais ('0002-09-01'), que só
// serviriam para varrer o banco inteiro — a data só entra no filtro quando fecha.
const isDataValida = (v: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) && Number(v.slice(0, 4)) >= 2000;

type AuditFilter = 'todos' | 'auditados' | 'nao_auditados';
type RepasseFilter = 'todos' | 'boleto_unico' | 'sucesso' | 'falha';

const isBoletoUnico = (f: Fatura): boolean =>
  !!f.status_repasse_equatorial && f.status_repasse_equatorial !== 'nao_aplicavel';

// "Pagar Equatorial agora" só onde o automático parou e a explicação manda usá-lo:
// repasse que falhou ou que esperava o código de barras. Boleto que não cobrou a conta
// de luz fica de fora, porque a conta é do cliente (ver explicarRepasse). Pago, na fila,
// débito automático e quitado pelo cliente também; a RPC recusaria de todo jeito.
const podePagarEquatorialAgora = (f: Fatura): boolean =>
  ['falhou', 'dados_indisponiveis'].includes(f.status_repasse_equatorial ?? '')
  && boletoCobrouEquatorial(f) !== false;

// Os filtros seguem a situação explicada, não o status cru: boleto que não cobrou a conta
// de luz não é falha de repasse, porque não havia o que repassar (ver explicarRepasse).
const FILTRO_POR_TOM: Record<TomRepasse, RepasseFilter | null> = {
  // "Repasse em andamento" saiu dos filtros em 21/10/2026, a pedido do Vinicius.
  ok: 'sucesso', erro: 'falha', andamento: null, neutro: null,
};

// Paleta cíclica para os chips de origem — parceiras são dinâmicas (hoje só
// Gran, mas o boleto único da própria Vigor deve trazer mais adiante), então
// não dá para hardcodar uma cor por nome.
const ORIGEM_CORES = [
  'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400',
  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
];

const TOM_REPASSE: Record<TomRepasse, { texto: string; ponto: string }> = {
  ok: { texto: 'text-emerald-700 dark:text-emerald-400', ponto: 'bg-emerald-500' },
  erro: { texto: 'text-red-700 dark:text-red-400', ponto: 'bg-red-500' },
  andamento: { texto: 'text-amber-700 dark:text-amber-400', ponto: 'bg-amber-500' },
  neutro: { texto: 'text-gray-600 dark:text-gray-300', ponto: 'bg-gray-400' },
};

const FILA_COLS = 'id, fatura_id, status, error_details, gateway, conta_pagadora, resultado, asaas_bill_id, valor_equatorial, '
  + 'baixa_em, baixa_por_nome, baixa_observacao, proxima_tentativa_em, created_at, updated_at';

// Abre o PDF em aba nova. A aba nasce no próprio clique, antes do await do link
// assinado: aberta depois, o navegador trata como pop-up e bloqueia.
const abrirEmAbaNova = async (resolverUrl: () => Promise<string | null>): Promise<boolean> => {
  const aba = window.open('', '_blank');
  const url = await resolverUrl().catch(() => null);
  if (!url) { aba?.close(); return false; }
  if (aba) {
    aba.opener = null;
    aba.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener');
  }
  return true;
};

// pdf_fatura já chegou como URL, como caminho no bucket e como string JSON ("\"url\"").
const abrirArquivo = (bruto: string): Promise<boolean> => abrirEmAbaNova(async () => {
  let url = bruto.trim();
  if (url.startsWith('"') && url.endsWith('"')) {
    try { url = JSON.parse(url); } catch { /* segue com a string crua */ }
  }
  if (/^https?:\/\//.test(url)) return url;
  const { data } = await supabase.storage.from('faturas').createSignedUrl(url, 60);
  return data?.signedUrl ?? null;
});

const BOTAO_DOC = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 '
  + 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 '
  + 'transition-[background-color,transform] duration-150 active:scale-[0.97]';

const BOTAO_PRIMARIO = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white '
  + 'hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed '
  + 'transition-[background-color,transform] duration-150 active:scale-[0.97]';

interface ParteDoPagamento { rotulo: string; detalhe: string; valor: number; cor: string }

const QUEM_PAGOU_POR_FORA = [
  { id: 'cliente', rotulo: 'Cliente pagou direto à Equatorial', exemplo: 'Ex.: cliente pagou a conta de luz na Equatorial em 18/10; como o boleto único também cobrou, devolver R$ 88,84 na próxima fatura.' },
  { id: 'financeiro', rotulo: 'Financeiro pagou pelo banco', exemplo: 'Ex.: paga pelo internet banking do Sicoob da Associação em 21/10, comprovante enviado no grupo.' },
  { id: 'outro', rotulo: 'Outro', exemplo: 'Explique quem pagou e como foi conferido.' },
] as const;

/**
 * Registra que a conta de luz foi paga fora do automático, com explicação obrigatória.
 * Usa a mesma baixa da tela Repasse Equatorial — manual (dar_baixa_repasse_equatorial),
 * que vale para item 'falhou' ou 'manual' e fecha fila e fatura na mesma transação.
 */
const RegistrarPagoPorFora: React.FC<{
  filaId: string; faturaId: string; onCancelar: () => void; onRegistrado: () => void;
}> = ({ filaId, faturaId, onCancelar, onRegistrado }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [quem, setQuem] = useState<(typeof QUEM_PAGOU_POR_FORA)[number]['id']>('cliente');
  const [data, setData] = useState(() => toInputDate(new Date()));
  const [explicacao, setExplicacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const opcao = QUEM_PAGOU_POR_FORA.find(o => o.id === quem)!;
  const explicacaoOk = explicacao.trim().length >= 10;

  const registrar = async () => {
    if (!explicacaoOk || !isDataValida(data)) return;
    setSalvando(true);
    try {
      const observacao = `${opcao.rotulo}: ${explicacao.trim()}`;
      const { error } = await supabase.rpc('dar_baixa_repasse_equatorial', { p_ids: [filaId], p_data: data, p_observacao: observacao });
      if (error) throw error;
      await logAction(profile?.id, 'REPASSE_EQUATORIAL_PAGO_POR_FORA', { fatura_id: faturaId, fila_id: filaId, quem, data, observacao });
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'auditoria_pagamentos', acao: 'registrar_pago_por_fora', origem: 'acao' });
      addToast('Pagamento por fora registrado.', 'success');
      onRegistrado();
    } catch (error: any) {
      console.error('Erro ao registrar pagamento por fora:', error);
      addToast(error?.message || 'Erro ao registrar o pagamento por fora.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 animate-fade-in">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Conta de luz paga por fora</p>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Fica registrado na fatura e o repasse deixa de aparecer como falha.</p>

      <fieldset className="mt-3">
        <legend className="text-xs font-semibold text-gray-500 dark:text-gray-400">Quem pagou</legend>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {QUEM_PAGOU_POR_FORA.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => setQuem(o.id)}
              aria-pressed={quem === o.id}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-[background-color,transform] duration-150 active:scale-[0.97] ${
                quem === o.id ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {o.rotulo}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mt-3 block">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Data do pagamento</span>
        <input
          type="date"
          value={data}
          onChange={e => setData(e.target.value)}
          className="mt-1 block text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg px-2 py-1.5 outline-none focus:border-primary"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Explicação</span>
        <textarea
          value={explicacao}
          onChange={e => setExplicacao(e.target.value)}
          rows={3}
          placeholder={opcao.exemplo}
          className="mt-1 block w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg px-3 py-2 outline-none focus:border-primary placeholder-gray-400 resize-y"
        />
        {!explicacaoOk && explicacao.length > 0 ? (
          <span className="mt-1 block text-[11px] text-amber-700 dark:text-amber-400">Escreva pelo menos 10 caracteres: é o registro do que aconteceu.</span>
        ) : null}
      </label>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={registrar}
          disabled={!explicacaoOk || !isDataValida(data) || salvando}
          className="px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,transform] duration-150 active:scale-[0.97]"
        >
          {salvando ? 'Registrando…' : 'Registrar'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          disabled={salvando}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

/**
 * "Pagar Equatorial agora" no próprio painel. Antes o botão só existia na fatura
 * (CobrancaEnviadaModal) e o painel mandava abrir a fatura para achá-lo; a Meryene
 * perguntou, em 03/11/2026, onde pedir o pagamento depois de conferir. Mesma RPC da
 * fatura: reativar_repasse_equatorial devolve o item à fila com as tentativas zeradas
 * e é dona das guardas (permissão, débito automático, quitado pelo cliente, repasse
 * já concluído). Pede confirmação porque move dinheiro.
 */
const PagarEquatorialAgora: React.FC<{
  faturaId: string; contaDeLuz: number | null; onCancelar: () => void; onEnfileirado: () => void;
}> = ({ faturaId, contaDeLuz, onCancelar, onEnfileirado }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [enviando, setEnviando] = useState(false);

  const pagar = async () => {
    setEnviando(true);
    try {
      const { error } = await supabase.rpc('reativar_repasse_equatorial', { p_fatura_id: Number(faturaId) });
      if (error) throw error;
      await logAction(profile?.id, 'PAGAR_EQUATORIAL_MANUAL', { fatura_id: faturaId, origem: 'auditoria_pagamentos' });
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'auditoria_pagamentos', acao: 'pagar_equatorial_manual', origem: 'acao' });
      addToast('Pagamento da conta de luz na fila: sai em poucos minutos, em dia útil das 8h às 18h.', 'success');
      onEnfileirado();
    } catch (error: any) {
      console.error('Erro ao enfileirar o pagamento da Equatorial:', error);
      addToast(error?.message || 'Erro ao enfileirar o pagamento da Equatorial.', 'error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 animate-fade-in">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
        {contaDeLuz != null
          ? <>Pagar <span className="tabular-nums">{formatCurrency(contaDeLuz)}</span> à Equatorial?</>
          : 'Pagar a conta de luz à Equatorial?'}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        Sai da conta em que o cliente pagou, em dia útil das 8h às 18h. Confira antes se a conta de luz continua em aberto.
      </p>
      <div className="mt-4 flex items-center gap-2">
        <button type="button" onClick={pagar} disabled={enviando} className={BOTAO_PRIMARIO}>
          {enviando ? 'Enviando…' : 'Pagar agora'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          disabled={enviando}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

interface DetalheFaturaProps {
  fatura: Fatura;
  origemLabel: string;
  dataPagamento: string | null;
  pago: number | null;
  partes: ParteDoPagamento[];
  avisoDivisao: string | null;
  explicacao: ReturnType<typeof explicarRepasse>;
  contaDeLuz: number | null;
  tentativas: PagamentoEquatorialAuditoria[] | undefined;
  carregandoTentativas: boolean;
  /** Item da fila em que dá para registrar pagamento por fora ('falhou' ou 'manual'); null esconde. */
  filaRegistravel: string | null;
  /** Repasse parado que o sistema pode tentar de novo (ver podePagarEquatorialAgora). */
  podePagarEquatorial: boolean;
  /** O repasse mudou (baixa por fora ou pagamento enfileirado): recarrega a lista. */
  onRegistrado: () => void;
}

/** Painel da linha aberta: para onde foi o dinheiro, o repasse à Equatorial e os documentos. */
const DetalheFatura: React.FC<DetalheFaturaProps> = ({
  fatura, origemLabel, dataPagamento, pago, partes, avisoDivisao, explicacao, contaDeLuz, tentativas, carregandoTentativas,
  filaRegistravel, podePagarEquatorial, onRegistrado,
}) => {
  const { addToast } = useToast();
  // Uma ação aberta de cada vez: a confirmação de pagar ou o registro por fora.
  const [acaoRepasse, setAcaoRepasse] = useState<'pagar' | 'por_fora' | null>(null);
  const fecharEAtualizar = () => { setAcaoRepasse(null); onRegistrado(); };
  const abrir = async (arquivo: string) => {
    if (!(await abrirArquivo(arquivo))) addToast('Não foi possível abrir o arquivo.', 'error');
  };
  // Mesma fonte das outras telas (linkBoleto): na Gran abre o boleto unificado
  // (Vigor + Equatorial), não a cobrança crua do Asaas.
  const abrirBoleto = async () => {
    if (!(await abrirEmAbaNova(() => resolverLinkBoleto(fatura)))) addToast('Não foi possível abrir o boleto.', 'error');
  };
  const temBoleto = !!(fatura.url_boleto || fatura.url_boleto_combinado || fatura.pdf_boleto_combinado);
  const total = partes.reduce((acc, p) => acc + p.valor, 0);
  const tom = explicacao ? TOM_REPASSE[explicacao.tom] : null;
  const linkFatura = `/financeiro/faturas?busca=${encodeURIComponent(fatura.uc_codigo || '')}`
    + (fatura.mes_referencia ? `&mes=${encodeURIComponent(fatura.mes_referencia)}` : '');

  return (
    <div className="grid gap-x-10 gap-y-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_minmax(0,1fr)] animate-fade-in">
      <section>
        <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Para onde foi o dinheiro</h4>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {pago != null
            ? <>Cliente pagou <strong className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatCurrency(pago)}</strong></>
            : 'Valor pago não registrado'}
          {dataPagamento ? <> em {formatDate(dataPagamento)}</> : null}
          <span className="text-gray-400"> · {origemLabel}</span>
        </p>
        {total > 0 ? (
          <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" role="img"
            aria-label={partes.map(p => `${p.rotulo} ${formatCurrency(p.valor)}`).join(', ')}>
            {partes.map(p => (
              <div key={p.rotulo} className={p.cor} style={{ width: `${(p.valor / total) * 100}%` }} />
            ))}
          </div>
        ) : null}
        <dl className="mt-3 space-y-2">
          {partes.map(p => (
            <div key={p.rotulo} className="flex items-baseline justify-between gap-3">
              <dt className="flex items-baseline gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 translate-y-[-1px] ${p.cor}`} />
                <span className="text-sm text-gray-700 dark:text-gray-200">{p.rotulo}</span>
                <span className="text-[11px] text-gray-400 truncate">{p.detalhe}</span>
              </dt>
              <dd className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{formatCurrency(p.valor)}</dd>
            </div>
          ))}
        </dl>
        {avisoDivisao ? <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{avisoDivisao}</p> : null}
      </section>

      <section>
        <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Repasse à Equatorial</h4>
        {explicacao && tom ? (
          <>
            <p className={`mt-2 flex items-center gap-2 text-sm font-semibold ${tom.texto}`}>
              <span className={`w-2 h-2 rounded-full ${tom.ponto}`} />
              {explicacao.titulo}
            </p>
            {explicacao.motivo ? <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{explicacao.motivo}</p> : null}
            {explicacao.acao ? (
              <div className="mt-3">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">O que fazer</p>
                <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{explicacao.acao}</p>
              </div>
            ) : null}
            {contaDeLuz != null ? (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Conta de luz <span className="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">{formatCurrency(contaDeLuz)}</span>
                {fatura.data_vencimento ? <> · vence {formatDate(fatura.data_vencimento)}</> : null}
              </p>
            ) : null}
            {!acaoRepasse && (podePagarEquatorial || filaRegistravel) ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {podePagarEquatorial ? (
                  <button type="button" onClick={() => setAcaoRepasse('pagar')} className={BOTAO_PRIMARIO}>
                    <ArrowRightLeft size={13} /> Pagar Equatorial agora
                  </button>
                ) : null}
                {filaRegistravel ? (
                  <button type="button" onClick={() => setAcaoRepasse('por_fora')} className={BOTAO_DOC}>
                    <CheckCircle2 size={13} /> Registrar pagamento por fora
                  </button>
                ) : null}
              </div>
            ) : null}
            {acaoRepasse === 'pagar' && podePagarEquatorial ? (
              <PagarEquatorialAgora
                faturaId={String(fatura.id)}
                contaDeLuz={contaDeLuz}
                onCancelar={() => setAcaoRepasse(null)}
                onEnfileirado={fecharEAtualizar}
              />
            ) : null}
            {acaoRepasse === 'por_fora' && filaRegistravel ? (
              <RegistrarPagoPorFora
                filaId={filaRegistravel}
                faturaId={String(fatura.id)}
                onCancelar={() => setAcaoRepasse(null)}
                onRegistrado={fecharEAtualizar}
              />
            ) : null}
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Sem boleto único: o cliente paga a conta de luz direto à Equatorial, e não há repasse.
          </p>
        )}
      </section>

      <section>
        <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Documentos</h4>
        <div className="mt-2 flex flex-wrap gap-2">
          {/* Aba nova: quem audita não perde a lista para ir ao Faturamento. */}
          <Link to={linkFatura} target="_blank" rel="noopener noreferrer" className={BOTAO_DOC} title="Abre a fatura no Faturamento, em outra aba">
            <ExternalLink size={13} /> Abrir fatura
          </Link>
          {fatura.pdf_fatura ? (
            <button type="button" onClick={() => abrir(fatura.pdf_fatura!)} className={BOTAO_DOC} title="PDF da conta de luz, em outra aba">
              <Zap size={13} /> Conta de luz
            </button>
          ) : null}
          {temBoleto ? (
            <button type="button" onClick={abrirBoleto} className={BOTAO_DOC} title="PDF do boleto que o cliente recebeu, em outra aba">
              <Receipt size={13} /> Boleto
            </button>
          ) : null}
        </div>

        {explicacao ? (
          <>
            <h4 className="mt-5 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Tentativas de pagamento</h4>
            {carregandoTentativas ? (
              <p className="mt-2 text-xs text-gray-400">Carregando…</p>
            ) : !tentativas || tentativas.length === 0 ? (
              <p className="mt-2 text-xs text-gray-400">Nenhuma tentativa registrada.</p>
            ) : (
              <ol className="mt-2 space-y-2.5">
                {tentativas.slice(0, 6).map(t => {
                  const ponto = t.sucesso === true ? 'bg-emerald-500' : t.sucesso === false ? 'bg-red-500' : 'bg-amber-500';
                  const resultado = t.consulta_status ? 'Consulta' : t.sucesso === true ? 'Pago' : t.sucesso === false ? 'Não pagou' : 'Em andamento';
                  const motivo = t.sucesso === true ? null : traduzirErroRepasse(t.error_details)?.motivo;
                  return (
                    <li key={t.id} className="flex gap-2 text-xs">
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${ponto}`} />
                      <div className="min-w-0">
                        <p className="text-gray-700 dark:text-gray-200">
                          <span className="tabular-nums">{new Date(t.executado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="text-gray-400"> · {t.gateway === 'sicoob' ? 'Sicoob' : 'Asaas'} · </span>
                          <span className="font-semibold">{resultado}</span>
                        </p>
                        {motivo ? <p className="text-gray-500 dark:text-gray-400 leading-snug">{motivo}</p> : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            {tentativas && tentativas.length > 6 ? (
              <p className="mt-2 text-[11px] text-gray-400">+ {tentativas.length - 6} tentativas mais antigas</p>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
};

const AuditoriaPagamentosView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const hasAccess = podeAuditarPagamentos(profile);

  const [dataInicio, setDataInicio] = useState(() => mesAtual().inicio);
  const [dataFim, setDataFim] = useState(() => mesAtual().fim);
  const [todoPeriodo, setTodoPeriodo] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [auditFilter, setAuditFilter] = useState<AuditFilter>('todos');
  const [repasseFilter, setRepasseFilter] = useState<RepasseFilter>('todos');
  const [origensExcluidas, setOrigensExcluidas] = useState<Set<string>>(new Set());
  const [gestaoUcKeys, setGestaoUcKeys] = useState<Set<string>>(new Set());
  const [empresasParceiras, setEmpresasParceiras] = useState<EmpresaParceira[]>([]);

  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [confirmarIds, setConfirmarIds] = useState<string[] | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tentativasPorFatura, setTentativasPorFatura] = useState<Record<string, PagamentoEquatorialAuditoria[]>>({});
  const [loadingTentativas, setLoadingTentativas] = useState<string | null>(null);
  // Item da fila que diz a situação do repasse de cada fatura (ver escolherItemRepasse).
  const [filaPorFatura, setFilaPorFatura] = useState<Record<string, PagamentoEquatorialFila>>({});

  const empresasParceirasMap = useMemo(() => {
    const map = new Map<string, EmpresaParceira>();
    empresasParceiras.forEach(e => map.set(e.id, e));
    return map;
  }, [empresasParceiras]);

  // 'Pago' tem data_pgto_boleto preenchida pelo webhook do banco; 'Quitado' (baixa via
  // cobrança avulsa/unificada) não passa por esse webhook e usa quitado_em como data de pagamento.
  const getDataPagamento = (f: Fatura): string | null => f.data_pgto_boleto || f.quitado_em || null;

  // Valor a considerar na auditoria: para faturas diretas da Vigor, o de sempre.
  // Para faturas de empresa parceira (hoje só a Gran), NÃO é o total do boleto
  // nem a soma Vigor+parceira — é o fixedValue que o Asaas de fato transferiu
  // para a carteira da Vigor no split (split_repasse_vigor). Se ainda não foi
  // calculado (parceira fora do fluxo Asaas, ou emissão antiga), retorna null —
  // a linha fica visível mas não auditável, em vez de contar um valor errado.
  //
  // Desde 26/09/2026 a cobrança da parceira nasce na conta da VIGOR e não há split
  // para ela: a parte da Vigor é o que o boleto cobra fora da Equatorial
  // (valor_boleto_vigor) menos o residual da parceira congelado na emissão — a
  // mesma conta que o split_repasse_vigor fazia quando a parceira emitia.
  const getValorConsiderado = (f: Fatura): number | null => {
    if (f.empresa_parceira_id && f.conta_asaas_emissora === 'vigor') {
      return f.split_valor_parceira != null
        ? Math.round((Number(f.valor_boleto_vigor || 0) - Number(f.split_valor_parceira)) * 100) / 100
        : null;
    }
    if (f.empresa_parceira_id) {
      return f.split_repasse_vigor != null ? Number(f.split_repasse_vigor) : null;
    }
    // Boleto único da Vigor: o valor pago traz a conta de luz junto, e ela sai de novo
    // para a Equatorial. Sem descontar, a auditoria (e a NF) contava a Equatorial como
    // receita — Deuselina (OUT/2026) aparecia com R$ 274,34 em vez de R$ 185,50.
    const equatorialNoBoleto = Number(f.valor_equatorial_no_boleto) || 0;
    if (f.valor_pagamento != null && equatorialNoBoleto > 0) {
      return Math.round((Number(f.valor_pagamento) - equatorialNoBoleto) * 100) / 100;
    }
    return Number(f.valor_pagamento ?? f.valor_boleto_vigor) || 0;
  };

  // Quanto da conta de luz o boleto pago cobrou: no da Vigor, a marca gravada pelo banco
  // quando a linha digitável chega; no da parceira (boleto único sempre somado), a conta
  // da distribuidora, salvo quando o cliente quitou por fora ou paga em débito automático.
  const getEquatorialCobrada = (f: Fatura): number => {
    if (!f.empresa_parceira_id) return Number(f.valor_equatorial_no_boleto) || 0;
    if (!isBoletoUnico(f) || f.equatorial_quitada_pelo_cliente || f.debito_automatico_equatorial) return 0;
    return Number(f.valor_fatura_distribuidora) || 0;
  };

  // O que o cliente pagou: o valor do banco ou, sem ele, a parte da Vigor mais a conta de luz.
  const getPago = (f: Fatura): number | null => {
    if (f.valor_pagamento != null) return Number(f.valor_pagamento);
    const valor = getValorConsiderado(f);
    return valor != null ? valor + getEquatorialCobrada(f) : null;
  };

  // O que ficou com a parceira (hoje só a Gran), em Detalhes e no Excel. Vem do valor
  // gravado na emissão (split_valor_parceira): é o que foi para a parceira de fato. A conta
  // "pago − Vigor − conta de luz" só serve de reserva — em OUT/2026 ela zerava a Gran em
  // boletos cujo valor pago não trazia a conta de luz somada (ex.: R$ 254,61 viravam 0).
  const getParteParceira = (f: Fatura): number => {
    if (!f.empresa_parceira_id) return 0;
    if (f.split_valor_parceira != null) return Number(f.split_valor_parceira);
    const valor = getValorConsiderado(f);
    const pago = getPago(f);
    if (pago == null || valor == null) return 0;
    return Math.max(0, Math.round((pago - valor - getEquatorialCobrada(f)) * 100) / 100);
  };

  const explicarRepasseDaFatura = (f: Fatura) =>
    explicarRepasse(f.status_repasse_equatorial, filaPorFatura[String(f.id)] ?? null, boletoCobrouEquatorial(f));

  const getOrigemKey = (f: Fatura): string => {
    if (f.empresa_parceira_id) return f.empresa_parceira_id;
    // A conta carimbada pela linha digitável é onde o dinheiro entrou; a UC só
    // decide para fatura antiga sem carimbo.
    if (isContaSicoob(f.conta_sicoob)) return f.conta_sicoob;
    return isGestaoFatura(f.uc_codigo, gestaoUcKeys) ? 'gestao' : 'associacao';
  };

  const getOrigemLabel = (key: string): string => {
    if (isContaSicoob(key)) return NOME_CONTA_SICOOB[key];
    return empresasParceirasMap.get(key)?.nome || 'Parceira';
  };

  const fetchFaturas = async () => {
    setLoading(true);
    try {
      const cols = [
        'id', 'cliente_nome', 'uc_codigo', 'mes_referencia', 'valor_boleto_vigor', 'valor_pagamento',
        'status_boleto_vigor', 'data_pgto_boleto', 'quitado_em', 'auditado_pagamento', 'auditado_pagamento_em',
        'auditado_pagamento_por_nome', 'empresa_parceira_id', 'split_repasse_vigor', 'status_repasse_equatorial',
        'conta_asaas_emissora', 'split_valor_parceira', 'conta_sicoob',
        'valor_equatorial_no_boleto', 'valor_fatura_distribuidora', 'equatorial_quitada_pelo_cliente',
        'debito_automatico_equatorial', 'data_vencimento', 'pdf_fatura', 'url_boleto', 'linha_digitavel_boleto',
        'url_boleto_combinado', 'pdf_boleto_combinado', 'emitido_em',
      ].join(', ');

      // Intervalo aberto dos dois lados: só 'De' traz tudo a partir da data, só
      // 'Até' traz tudo antes dela, e os dois vazios equivalem a todo o período.
      let periodoFilter = '';
      if (!todoPeriodo) {
        const inicio = isDataValida(dataInicio) ? `${dataInicio}T00:00:00` : null;
        const fim = isDataValida(dataFim) ? `${dataFim}T23:59:59` : null;
        if (inicio || fim) {
          const intervalo = (col: string) => {
            const partes: string[] = [];
            if (inicio) partes.push(`${col}.gte.${inicio}`);
            if (fim) partes.push(`${col}.lte.${fim}`);
            return `and(${partes.join(',')})`;
          };
          periodoFilter = `${intervalo('data_pgto_boleto')},${intervalo('quitado_em')}`;
        }
      }

      // O PostgREST limita cada resposta a 1000 linhas. A 1ª página traz o total
      // e as demais saem em paralelo — antes era uma página de cada vez.
      // O select é uma string montada (cols), que o parser de tipos do postgrest-js
      // não resolve sem um schema tipado — cai num tipo genérico em vez de `any`.
      // `unknown` primeiro evita o TS2352 sem precisar tipar o client inteiro.
      const allData = (await buscarTudoPorChave<any>(
        (apos, tamanho) => {
          let query = supabase
            .from('faturas')
            .select(cols)
            .in('status_boleto_vigor', ['Pago', 'Quitado'])
            .neq('status_fatura', 'Excluído')
            .order('id', { ascending: true })
            .limit(tamanho);
          if (apos != null) query = query.gt('id', apos);
          if (periodoFilter) query = query.or(periodoFilter);
          return query as any;
        },
        (f) => f.id,
      )) as unknown as Fatura[];

      const sorted = allData.sort((a, b) => {
        const da = getDataPagamento(a);
        const db = getDataPagamento(b);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return new Date(db).getTime() - new Date(da).getTime();
      });
      setFaturas(sorted);
      await carregarFila(sorted);
    } catch (error) {
      console.error('Error fetching faturas for auditoria:', error);
      addToast('Erro ao carregar boletos pagos.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Só as faturas com repasse têm item na fila. Lotes de 150 ids cabem na URL do
  // PostgREST e saem em paralelo. Falhar aqui não derruba a lista: a coluna de repasse
  // mostra o que der a partir de status_repasse_equatorial.
  const carregarFila = async (lista: Fatura[]) => {
    const ids = lista.filter(isBoletoUnico).map(f => f.id);
    const lotes: string[][] = [];
    for (let i = 0; i < ids.length; i += 150) lotes.push(ids.slice(i, i + 150));
    const respostas = await Promise.all(lotes.map(lote =>
      supabase.from('pagamento_equatorial_fila').select(FILA_COLS).in('fatura_id', lote)));
    const porFatura = new Map<string, PagamentoEquatorialFila[]>();
    for (const { data, error } of respostas) {
      if (error) { console.error('Erro ao carregar a fila de repasse:', error); continue; }
      // FILA_COLS é concatenada, e o parser de tipos do postgrest-js só entende literal.
      for (const item of (data as unknown as PagamentoEquatorialFila[]) || []) {
        const chave = String(item.fatura_id);
        porFatura.set(chave, [...(porFatura.get(chave) ?? []), item]);
      }
    }
    const escolhidos: Record<string, PagamentoEquatorialFila> = {};
    porFatura.forEach((itens, chave) => {
      const item = escolherItemRepasse(itens);
      if (item) escolhidos[chave] = item;
    });
    setFilaPorFatura(escolhidos);
  };

  // O <input type="date"> dispara onChange a cada parte digitada; o debounce
  // evita uma consulta por tecla enquanto o período está sendo preenchido.
  useEffect(() => {
    if (!hasAccess) return;
    const t = setTimeout(() => { fetchFaturas(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicio, dataFim, todoPeriodo, hasAccess]);

  useEffect(() => {
    if (hasAccess) fetchGestaoUcKeys().then(setGestaoUcKeys).catch(() => {});
  }, [hasAccess]);

  useEffect(() => {
    if (!hasAccess) return;
    supabase.from('empresas_parceiras').select('id, nome, banco_conta').then(({ data, error }) => {
      if (!error) setEmpresasParceiras((data as EmpresaParceira[]) || []);
    });
  }, [hasAccess]);

  const handleMesAtual = () => {
    const { inicio, fim } = mesAtual();
    setDataInicio(inicio);
    setDataFim(fim);
  };

  const origensPresentes = useMemo(() => {
    const map = new Map<string, string>();
    faturas.forEach(f => {
      const key = getOrigemKey(f);
      if (!map.has(key)) map.set(key, getOrigemLabel(key));
    });
    return Array.from(map.entries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faturas, gestaoUcKeys, empresasParceirasMap]);

  const origemCorPorKey = useMemo(() => {
    const map = new Map<string, string>();
    origensPresentes.forEach(([key], i) => map.set(key, ORIGEM_CORES[i % ORIGEM_CORES.length]));
    return map;
  }, [origensPresentes]);

  const toggleOrigem = (key: string) => {
    setOrigensExcluidas(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const filteredFaturas = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return faturas.filter(f => {
      if (auditFilter === 'auditados' && !f.auditado_pagamento) return false;
      if (auditFilter === 'nao_auditados' && f.auditado_pagamento) return false;

      if (origensExcluidas.has(getOrigemKey(f))) return false;

      if (repasseFilter !== 'todos') {
        if (!isBoletoUnico(f)) return false;
        if (repasseFilter === 'boleto_unico') {
          if (boletoCobrouEquatorial(f) === false) return false;
        } else {
          const explicacao = explicarRepasseDaFatura(f);
          if (!explicacao || FILTRO_POR_TOM[explicacao.tom] !== repasseFilter) return false;
        }
      }

      if (term) {
        const haystack = `${f.cliente_nome || ''} ${f.uc_codigo || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faturas, searchTerm, auditFilter, repasseFilter, origensExcluidas, gestaoUcKeys, filaPorFatura]);

  useEffect(() => {
    setSelectedIds(prev => {
      const validIds = new Set(filteredFaturas.map(f => f.id));
      const next = new Set(Array.from(prev).filter(id => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredFaturas]);

  const allSelected = filteredFaturas.length > 0 && selectedIds.size === filteredFaturas.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filteredFaturas.map(f => f.id)));
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const executarDesfazerAuditoria = async (ids: string[]) => {
    try {
      const { error } = await supabase.from('faturas').update({
        auditado_pagamento: false,
        auditado_pagamento_em: null,
        auditado_pagamento_por_id: null,
        auditado_pagamento_por_nome: null,
        auditado_pagamento_nf_id: null,
      }).in('id', ids);
      if (error) throw error;

      await logAction(profile?.id, ids.length > 1 ? 'DESFAZER_AUDITORIA_PAGAMENTO_LOTE' : 'DESFAZER_AUDITORIA_PAGAMENTO', {
        fatura_ids: ids,
        quantidade: ids.length,
      });
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'auditoria_pagamentos', acao: 'desfazer_auditoria', origem: 'acao' });
      addToast(`${ids.length} boleto(s) com auditoria desfeita.`, 'success');
      setSelectedIds(new Set());
      await fetchFaturas();
    } catch (error) {
      console.error('Error undoing auditado_pagamento:', error);
      addToast('Erro ao desfazer auditoria.', 'error');
    }
  };

  const handleToggleOne = async (fatura: Fatura) => {
    if (fatura.auditado_pagamento) {
      setSavingId(fatura.id);
      await executarDesfazerAuditoria([fatura.id]);
      setSavingId(null);
    } else {
      setConfirmarIds([fatura.id]);
    }
  };

  const handleBulkApply = async (value: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    if (!value) {
      setBulkSaving(true);
      await executarDesfazerAuditoria(ids);
      setBulkSaving(false);
      return;
    }

    const auditaveis = ids.filter(id => {
      const f = faturas.find(x => x.id === id);
      return !!f && !f.auditado_pagamento && getValorConsiderado(f) !== null;
    });
    const ignorados = ids.length - auditaveis.length;
    if (auditaveis.length === 0) {
      addToast('Nenhum boleto selecionado pode ser auditado (já auditado(s) ou sem valor da Vigor calculado).', 'error');
      return;
    }
    if (ignorados > 0) {
      addToast(`${ignorados} boleto(s) ignorado(s) — já auditado(s) ou sem valor da Vigor calculado.`, 'info');
    }
    setConfirmarIds(auditaveis);
  };

  const toggleExpand = async (fatura: Fatura) => {
    if (expandedId === fatura.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(fatura.id);
    if (isBoletoUnico(fatura) && !tentativasPorFatura[fatura.id]) {
      setLoadingTentativas(fatura.id);
      try {
        const { data, error } = await supabase
          .from('pagamento_equatorial_auditoria')
          .select('*')
          .eq('fatura_id', fatura.id)
          .order('executado_em', { ascending: false });
        if (error) throw error;
        setTentativasPorFatura(prev => ({ ...prev, [fatura.id]: (data as PagamentoEquatorialAuditoria[]) || [] }));
      } catch (error) {
        console.error('Error fetching tentativas de repasse:', error);
        addToast('Erro ao carregar tentativas de repasse à Equatorial.', 'error');
      } finally {
        setLoadingTentativas(null);
      }
    }
  };

  if (!hasAccess) {
    return <Navigate to="/financeiro/faturas" replace />;
  }

  const valoresConsiderados = filteredFaturas.map(f => ({ f, valor: getValorConsiderado(f) }));
  const totalPago = valoresConsiderados.reduce((acc, { valor }) => acc + (valor ?? 0), 0);
  const totalAuditado = filteredFaturas.filter(f => f.auditado_pagamento).length;
  const totalNaoAuditavel = filteredFaturas.filter(f => !f.auditado_pagamento && getValorConsiderado(f) === null).length;

  const handleExportExcel = () => {
    const headers = [
      'Cliente', 'UC', 'Origem', 'Mês Referência', 'Valor considerado — Vigor (R$)', 'Equatorial no boleto (R$)',
      'Parte da parceira (R$)', 'Data Pagamento', 'Status', 'Repasse à Equatorial', 'Motivo do repasse',
      'Auditado', 'Auditado por', 'Auditado em',
    ];
    const rows = filteredFaturas.map(f => {
      const valor = getValorConsiderado(f);
      const explicacao = explicarRepasseDaFatura(f);
      return [
        f.cliente_nome ?? '',
        f.uc_codigo ?? '',
        getOrigemLabel(getOrigemKey(f)),
        f.mes_referencia ?? '',
        valor ?? 0,
        getEquatorialCobrada(f) || '',
        f.empresa_parceira_id ? getParteParceira(f) : '',
        getDataPagamento(f) ? new Date(getDataPagamento(f)!).toLocaleDateString('pt-BR') : '',
        f.status_boleto_vigor ?? '',
        explicacao?.titulo ?? '',
        explicacao?.motivo ?? '',
        f.auditado_pagamento ? 'Sim' : 'Não',
        f.auditado_pagamento_por_nome ?? '',
        f.auditado_pagamento_em ? new Date(f.auditado_pagamento_em).toLocaleDateString('pt-BR') : '',
      ];
    });
    const totalParceira = filteredFaturas.reduce((acc, f) => acc + getParteParceira(f), 0);
    const totals = [
      `TOTAL (${filteredFaturas.length} boletos)`, '', '', '', totalPago, '', totalParceira, '', '', '', '', '', '', '',
    ];
    const ws = buildSheet(
      headers, rows,
      ['txt', 'txt', 'txt', 'txt', 'num', 'num', 'num', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt', 'txt'],
      { totals, widths: [35, 14, 18, 14, 20, 16, 18, 16, 14, 24, 60, 10, 25, 16] },
    );
    downloadWorkbook([{ name: 'Auditoria de Pagamentos', ws }], `Auditoria_Pagamentos_${fileStamp()}.xlsx`);
  };

  return (
    <>
    <div className="animate-fade-in space-y-6 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-poppins font-bold dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-lg shadow-primary/20">
              <ShieldCheck size={20} className="text-white" />
            </div>
            Auditoria de Pagamentos
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px]">
            Confira boletos pagos (Vigor e empresas parceiras) e marque como auditados para o cálculo de comissão
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Boletos pagos no período</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{filteredFaturas.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Valor total (Vigor)</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(totalPago)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Já auditados</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{totalAuditado} / {filteredFaturas.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sem valor calculado</p>
          <p className={`text-2xl font-bold mt-1 ${totalNaoAuditavel > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}>{totalNaoAuditavel}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar por cliente ou UC..."
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div className="flex items-center gap-1 bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            {([
              { id: 'todos', label: 'Todos' },
              { id: 'nao_auditados', label: 'Não auditados' },
              { id: 'auditados', label: 'Auditados' },
            ] as { id: AuditFilter; label: string }[]).map(opt => (
              <button
                key={opt.id}
                onClick={() => setAuditFilter(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                  auditFilter === opt.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap cursor-pointer select-none">
            <input
              type="checkbox"
              checked={todoPeriodo}
              onChange={e => setTodoPeriodo(e.target.checked)}
              className="rounded text-primary focus:ring-primary"
            />
            <Calendar size={14} />
            Ver todos os períodos
          </label>

          <div className={`flex flex-wrap items-center gap-2 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${todoPeriodo ? 'opacity-50' : ''}`}>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Período:</span>
            <input
              type="date"
              value={dataInicio}
              disabled={todoPeriodo}
              onChange={e => setDataInicio(e.target.value)}
              title="Pagamentos a partir desta data"
              className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary disabled:cursor-not-allowed"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={dataFim}
              disabled={todoPeriodo}
              onChange={e => setDataFim(e.target.value)}
              title="Pagamentos até esta data"
              className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary disabled:cursor-not-allowed"
            />
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
            <button
              onClick={handleMesAtual}
              disabled={todoPeriodo}
              className="flex items-center gap-2 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors disabled:cursor-not-allowed whitespace-nowrap"
            >
              <Calendar size={14} />
              Mês Atual
            </button>
          </div>

          <button
            onClick={fetchFaturas}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Atualizar"
          >
            <RefreshCw size={16} className="text-gray-500 dark:text-gray-400" />
          </button>

          <button
            onClick={handleExportExcel}
            disabled={filteredFaturas.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            title="Exportar para Excel"
          >
            <Download size={14} />
            Exportar Excel
          </button>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex flex-wrap items-center gap-1 bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            {origensPresentes.map(([key, label], i) => {
              const ativo = !origensExcluidas.has(key);
              const cor = ORIGEM_CORES[i % ORIGEM_CORES.length];
              return (
                <button
                  key={key}
                  onClick={() => toggleOrigem(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                    ativo ? cor : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  title={`Filtrar boletos recebidos pela ${label}`}
                >
                  <Building2 size={14} /> {label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-1 bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            {([
              { id: 'todos', label: 'Todos' },
              { id: 'boleto_unico', label: 'Boleto único' },
              { id: 'sucesso', label: 'Repasse OK' },
              { id: 'falha', label: 'Repasse falhou' },
            ] as { id: RepasseFilter; label: string }[]).map(opt => (
              <button
                key={opt.id}
                onClick={() => setRepasseFilter(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                  repasseFilter === opt.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner label="Carregando boletos pagos..." />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="overflow-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[1180px]">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-4 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="rounded text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Cliente / UC</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Mês Referência</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right">Valor (Vigor)</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Data Pagamento</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Repasse à Equatorial</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Auditoria</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {filteredFaturas.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">
                      Nenhum boleto pago encontrado com os filtros atuais.
                    </td>
                  </tr>
                ) : (
                  filteredFaturas.map(fatura => {
                    const valor = getValorConsiderado(fatura);
                    const naoAuditavel = !fatura.auditado_pagamento && valor === null;
                    const origemLabel = getOrigemLabel(getOrigemKey(fatura));
                    const boletoUnico = isBoletoUnico(fatura);
                    const itemFila = filaPorFatura[String(fatura.id)] ?? null;
                    const cobrouEquatorial = boletoCobrouEquatorial(fatura);
                    const explicacao = explicarRepasseDaFatura(fatura);
                    const tom = explicacao ? TOM_REPASSE[explicacao.tom] : null;
                    const equatorialCobrada = getEquatorialCobrada(fatura);
                    const contaDeLuz = boletoUnico
                      ? (Number(itemFila?.valor_equatorial ?? fatura.valor_equatorial_no_boleto ?? fatura.valor_fatura_distribuidora) || null)
                      : null;
                    const expandido = expandedId === fatura.id;

                    return (
                      <React.Fragment key={fatura.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(fatura.id)}
                              onChange={() => toggleSelectOne(fatura.id)}
                              className="rounded text-primary focus:ring-primary"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-bold text-gray-900 dark:text-white">{fatura.cliente_nome}</span>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{fatura.uc_codigo}</span>
                                <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                  origemCorPorKey.get(getOrigemKey(fatura)) || 'bg-gray-100 text-gray-600'
                                }`}>
                                  {origemLabel}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{fatura.mes_referencia}</span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {valor !== null ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{formatCurrency(valor)}</span>
                                {equatorialCobrada > 0 ? (
                                  <span className="text-[11px] text-gray-400 tabular-nums" title="Conta de luz cobrada no mesmo boleto e repassada à Equatorial">
                                    + {formatCurrency(equatorialCobrada)} Equatorial
                                  </span>
                                ) : null}
                                {!!fatura.empresa_parceira_id && (
                                  <span className="text-[10px] text-gray-400">valor repassado à Vigor (split)</span>
                                )}
                              </div>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400"
                                title="Split para a Vigor ainda não calculado nesta fatura — não é possível auditar."
                              >
                                <AlertTriangle size={12} /> —
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1 items-start">
                              <span className="text-sm text-gray-600 dark:text-gray-300">{formatDate(getDataPagamento(fatura))}</span>
                              {fatura.status_boleto_vigor === 'Quitado' && (
                                <span
                                  className="inline-flex items-center gap-1 w-fit text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                                  title="Boleto quitado via cobrança avulsa/unificada — não passa pelo webhook do banco, por isso a data vem de quitado_em em vez de data_pgto_boleto."
                                >
                                  Quitado via cobrança avulsa
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <button
                              type="button"
                              onClick={() => toggleExpand(fatura)}
                              aria-expanded={expandido}
                              className="group block w-full max-w-[280px] text-left rounded-lg -mx-2 px-2 py-1 hover:bg-gray-100/70 dark:hover:bg-gray-700/40 transition-colors"
                            >
                              {explicacao && tom ? (
                                <>
                                  <span className={`flex items-center gap-1.5 text-xs font-semibold ${tom.texto}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tom.ponto}`} />
                                    {explicacao.titulo}
                                    {contaDeLuz != null && cobrouEquatorial !== false ? (
                                      <span className="font-normal text-gray-400 tabular-nums">· {formatCurrency(contaDeLuz)}</span>
                                    ) : null}
                                  </span>
                                  {explicacao.motivo ? (
                                    <span className={`mt-0.5 block text-[11px] leading-snug ${explicacao.tom === 'ok' || explicacao.tom === 'neutro' ? 'text-gray-400 line-clamp-1' : 'text-gray-600 dark:text-gray-300 line-clamp-2'}`}>
                                      {explicacao.motivo}
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                <span className="block text-xs text-gray-400">Sem boleto único</span>
                              )}
                              <span className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary">
                                {expandido ? 'Fechar' : 'Detalhes'}
                                <ChevronDown size={12} className={`transition-transform duration-200 ${expandido ? 'rotate-180' : ''}`} />
                              </span>
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            {fatura.auditado_pagamento ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 w-fit">
                                  <CheckCircle2 size={12} /> Auditado
                                </span>
                                {fatura.auditado_pagamento_por_nome && (
                                  <span className="text-[10px] text-gray-400">
                                    por {fatura.auditado_pagamento_por_nome} em {formatDate(fatura.auditado_pagamento_em)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold w-fit ${
                                naoAuditavel
                                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}>
                                <Circle size={12} /> {naoAuditavel ? 'Não auditável' : 'Pendente'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleToggleOne(fatura)}
                              disabled={savingId === fatura.id || naoAuditavel}
                              title={naoAuditavel ? 'Split para a Vigor ainda não calculado nesta fatura.' : undefined}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                fatura.auditado_pagamento
                                  ? 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  : 'bg-primary/10 text-primary hover:bg-primary/20'
                              }`}
                            >
                              {fatura.auditado_pagamento ? 'Desfazer' : 'Marcar auditado'}
                            </button>
                          </td>
                        </tr>
                        {expandido && (() => {
                          const pago = getPago(fatura);
                          const parceira = getParteParceira(fatura);
                          const partes: ParteDoPagamento[] = [];
                          if (valor != null) partes.push({ rotulo: 'Vigor', detalhe: 'base da auditoria', valor, cor: 'bg-primary' });
                          if (equatorialCobrada > 0) partes.push({ rotulo: 'Equatorial', detalhe: 'conta de luz, repassada', valor: equatorialCobrada, cor: 'bg-gray-400 dark:bg-gray-500' });
                          if (parceira > 0) partes.push({ rotulo: origemLabel, detalhe: 'parte da parceira', valor: parceira, cor: 'bg-secondary' });
                          const aviso = valor == null
                            ? 'A parte da Vigor ainda não foi calculada para esta fatura.'
                            : boletoUnico && !fatura.empresa_parceira_id && equatorialCobrada === 0
                              ? 'O boleto pago não cobrou a conta de luz: o cliente pagou só a parte da Vigor.'
                              : null;
                          return (
                            <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                              <td colSpan={8} className="px-6 py-5">
                                <DetalheFatura
                                  fatura={fatura}
                                  origemLabel={origemLabel}
                                  dataPagamento={getDataPagamento(fatura)}
                                  pago={pago}
                                  partes={partes}
                                  avisoDivisao={aviso}
                                  explicacao={explicacao}
                                  contaDeLuz={contaDeLuz}
                                  tentativas={tentativasPorFatura[fatura.id]}
                                  carregandoTentativas={loadingTentativas === fatura.id}
                                  filaRegistravel={itemFila && (itemFila.status === 'falhou' || itemFila.status === 'manual') ? itemFila.id : null}
                                  podePagarEquatorial={podePagarEquatorialAgora(fatura)}
                                  onRegistrado={() => {
                                    // A baixa ou o pagamento novo mudam o histórico: recarrega ao abrir de novo.
                                    setTentativasPorFatura(prev => { const { [fatura.id]: _, ...resto } = prev; return resto; });
                                    fetchFaturas();
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-xs text-gray-400 text-center">
            Total de {filteredFaturas.length} boleto(s) listado(s)
          </div>
        </div>
      )}

    </div>

    {/* Bulk action bar — portaled to <body> so it stays fixed to the viewport
        instead of the containing block created by an ancestor's animate-fade-in transform */}
    {selectedIds.size > 0 && createPortal(
      <div className="fixed inset-x-0 bottom-0 z-[1000] px-3 pb-3 pointer-events-none flex justify-center">
        <div className="pointer-events-auto w-full sm:w-auto bg-gray-900 dark:bg-gray-950 text-white rounded-2xl shadow-2xl border border-white/10 px-3 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 animate-fade-in">
          <div className="flex items-center gap-2 pr-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold shrink-0">{selectedIds.size}</span>
            <p className="text-xs font-bold whitespace-nowrap">
              {selectedIds.size === 1 ? '1 boleto selecionado' : `${selectedIds.size} boletos selecionados`}
            </p>
          </div>
          <div className="h-7 w-px bg-white/15 hidden sm:block" />
          <button
            onClick={() => handleBulkApply(true)}
            disabled={bulkSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/90 hover:bg-emerald-500 transition-colors disabled:opacity-50"
          >
            <Check size={14} /> {bulkSaving ? 'Aplicando...' : 'Marcar como auditado'}
          </button>
          <button
            onClick={() => handleBulkApply(false)}
            disabled={bulkSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            Desfazer auditoria
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1.5 rounded-full text-white/70 hover:bg-white/10 transition-colors"
            title="Limpar seleção"
          >
            <X size={16} />
          </button>
        </div>
      </div>,
      document.body
    )}

    {confirmarIds && (
      <ConfirmarAuditoriaModal
        faturaIds={confirmarIds}
        valorTotal={confirmarIds.reduce((acc, id) => {
          const f = faturas.find(x => x.id === id);
          return acc + (f ? (getValorConsiderado(f) ?? 0) : 0);
        }, 0)}
        onClose={() => setConfirmarIds(null)}
        onConfirmed={() => {
          setConfirmarIds(null);
          setSelectedIds(new Set());
          fetchFaturas();
        }}
      />
    )}
    </>
  );
};

export default AuditoriaPagamentosView;
